/**
 * src/utils/formatters.js
 *
 * Centralized formatting helpers for the entire frontend.
 * Import from here instead of defining ad-hoc formatters in page components.
 */

/* ─── Currency ────────────────────────────────────────────────────────── */

/**
 * Format a centavo value to a Philippine Peso display string.
 * @param {number|string|null} cents - Amount in centavos (e.g. 15000 → ₱150.00)
 * @returns {string}
 */
export function fmtPeso(cents) {
  return `₱${(Number(cents || 0) / 100).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format a whole-peso value (NOT cents) with ₱ symbol.
 * Useful for product display prices that are already in peso units.
 * @param {number|string|null} amount - Amount in pesos
 * @returns {string}
 */
export function fmtPesoWhole(amount) {
  const num = Number(amount || 0);
  if (!Number.isFinite(num)) return "₱0";
  return `₱${num.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/* ─── Dates ───────────────────────────────────────────────────────────── */

/**
 * Format an ISO date string for short display (e.g. "Mar 23, 2026").
 * Returns "—" for null/invalid values.
 * @param {string|null} isoStr
 * @returns {string}
 */
export function fmtDate(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/**
 * Format an ISO date string for full date+time display (e.g. "Mar 23, 2026, 2:30 PM").
 * Returns "—" for null/invalid values.
 * @param {string|null} isoStr
 * @returns {string}
 */
export function fmtDateTime(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ─── File Size ───────────────────────────────────────────────────────── */

/**
 * Format a file size in bytes to a human-readable string.
 * @param {number} bytes
 * @returns {string}
 */
export function fmtFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
